import React from "react";

import { Alert, Box, Grid } from "@mui/material";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";

import { LoadingSkeletons } from "./LoadingSkeletons";
import Headerbar from "../../common/Headerbar";
import Breadcrumbs from "../../common/Breadcrumbs";

function MainContentLayout({
  children,
  error,
  isLoading,
  renderVideoCards,
  episodeName,
  episodeId,
  handleMobileMenuToggle,
}: {
  children: React.ReactNode;
  error: boolean;
  isLoading: boolean;
  renderVideoCards: () => React.ReactNode;
  episodeName: string;
  episodeId: string;
  handleMobileMenuToggle: () => void;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        minWidth: 0, // Prevents flex item from overflowing
      }}
    >
      {/* Header */}
      <Headerbar
        pageName={`Episode - ${episodeName}`}
        onMobileMenuClick={handleMobileMenuToggle}
      />

      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          {
            label: "Episodes",
            path: "/episodes",
            icon: <PlaylistPlayIcon sx={{ fontSize: 18 }} />,
          },
          {
            label: episodeName || "Episode",
            path: `/episodes/${episodeId}`,
          },
          {
            label: "Short Videos",
            path: `/episodes/${episodeId}/videos`,
            icon: <OndemandVideoIcon sx={{ fontSize: 18 }} />,
          },
        ]}
        showHome={false}
      />
      <Box
        sx={{
          flex: 1,
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          overflow: "auto",
          px: { xs: 1, sm: 2, md: 3, lg: 4 },
          py: { xs: 2, sm: 3, md: 4, lg: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: { xs: 2, md: 3 },
            mt: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 2, sm: 3 },
          }}
        >
          {children}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load videos. Please try again.
          </Alert>
        )}

        <Grid
          container
          spacing={{ xs: 1.5, sm: 2, md: 3, lg: 4 }}
          sx={{
            mt: { xs: 1, sm: 2 },
            mb: { xs: 2, sm: 3 },
          }}
        >
          {isLoading ? <LoadingSkeletons /> : renderVideoCards()}
        </Grid>
      </Box>
    </Box>
  );
}

export { MainContentLayout };
